import { useCallback, useMemo } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { createHybridTheme, cn } from './hybrid';
import type { ThemeName } from './tokens';

export function useHybridTheme() {
  const { currentTheme } = useSettings();
  const theme = currentTheme as ThemeName;
  const hybridTheme = useMemo(() => createHybridTheme(theme), [theme]);

  // Helper to get classes with theme overrides
  const getClasses = useCallback(
    (baseClasses: string) => {
      return hybridTheme.getClasses(baseClasses);
    },
    [hybridTheme]
  );

  // Helper to get a specific component color
  const getColor = useCallback((path: string) => {
    return `var(--${path.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()})`;
  }, []);

  return {
    theme,
    colors: hybridTheme.colors,
    getClasses,
    getColor,
    cn,
  };
}

// Example usage in a component:
/*
function Sidebar() {
  const { getClasses, getColor } = useHybridTheme();
  
  return (
    <div 
      // During migration: Use getClasses to support all themes
      className={getClasses('bg-transparent-light dark:bg-transparent-dark')}
      // After migration: Use CSS variables
      style={{ 
        backgroundColor: getColor('sidebar-background')
      }}
    >
      {children}
    </div>
  );
}
*/
